import { useEffect, useMemo, useRef } from 'react'

import { highlightIn } from '../lib/highlight'
import { hasOpenFence, splitStable } from '../lib/incremental'
import { hasMath, renderMathInElement } from '../lib/math'
import { renderMarkdown } from '../lib/sanitize'

/**
 * Markdown blogu - ARTIMLI, ve tek sanitize bogaz noktasindan geciyor.
 *
 * ## `dangerouslySetInnerHTML` bilincli
 *
 * Tek gerekcesi su: `renderMarkdown` hattaki TEK bogaz noktasi ve icinde
 * sabit allowlist'li `DOMPurify.sanitize` kosuyor (bkz. `lib/sanitize.ts`).
 * Baska hicbir yerde ham HTML DOM'a verilmiyor; verilecek olsa bu yorum
 * kalkmali.
 *
 * ## Artimli render: O(n^2) -> O(n)
 *
 * Her frame'de TUM birikmis string uzerinde `marked.parse` + `DOMPurify` +
 * KaTeX + hljs kosturmak frame basina O(n), yani yanit basina O(n^2). 8 KB
 * ve 60 fps'de gorunur jank esigi.
 *
 * Cozum, markdown'in blok yapisindan geliyor: **son ust-seviye blok
 * sinirindan oncesi, metin eklendikce DEGISEMEZ.** Kararli onek bir kez
 * render edilip `useMemo` ile onbellege aliniyor; yalnizca kuyruk her frame
 * yeniden parse ediliyor ve o birkac yuz byte.
 *
 * ## Fence paritesi kod tabanindaki en kritik satir
 *
 * `splitStable` bos satirlari sinir sayiyor AMA acik bir ``` blogunun
 * icindekileri saymiyor. Bu olmadan bos satir iceren bir kod blogu akis
 * ortasinda paragraflara DAGILIR. `incremental.ts` bunu tek gecisle yapiyor
 * ve ayri ayri test edilmis (bos satirli fence, kapanmamis, listede nested,
 * `~~~`, girintili).
 *
 * ## Renklendirme efekti NEDEN her render'da kosuyor
 *
 * React, `dangerouslySetInnerHTML`i deger DEGISMESE BILE bu fiber'i
 * etkileyen HER commit'te DOM'a yeniden yaziyor (olcumle dogrulandi: bir ust
 * bilesen - orn. oturum/akis durumu - alakasiz bir sebeple yeniden
 * render olunca `stableHtml` ayni kalsa da innerHTML SIFIRDAN yaziliyor ve
 * `highlightIn`in eklediği hljs span'lari/kopyalama dugmesi siliniyor).
 * Efekt `[stableHtml, parts.stable]`e bagli kalsaydi bu sifirlamayi ASLA
 * fark etmezdi (kendi girdisi degismedi). Bagimlilik dizisi OLMADAN her
 * render sonrasi calismasi bunun icin sart - `highlightIn` zaten
 * `dataset.highlighted`/`copyReady` ile idempotent, yani tekrar tekrar
 * cagirmak ucuz bir no-op, ama SIFIRLANMIS bir DOM'u onarmanin TEK yolu bu.
 */
export function Markdown({ text, streaming = false }: { text: string; streaming?: boolean }) {
  const stableRef = useRef<HTMLDivElement>(null)
  const tailRef = useRef<HTMLDivElement>(null)

  const parts = useMemo(() => splitStable(text), [text])

  // Kararli onek: `parts.stable` degismedikce YENIDEN PARSE EDILMIYOR.
  // Buyuyen bir yanitta bu deger uzun sure ayni kaliyor.
  const stableHtml = useMemo(() => renderMarkdown(parts.stable), [parts.stable])
  const tailHtml = useMemo(() => renderMarkdown(parts.tail), [parts.tail])

  // Kuyrukta acik fence varsa renklendirme ATLANIYOR: fence kapanana kadar
  // dil bile bilinmiyor ve her frame yeniden renklendirmek titreme uretiyor
  // (vanilla arayuzdeki gorunur sorun tam buydu).
  const tailOpen = hasOpenFence(parts.tail)

  useEffect(() => {
    const node = stableRef.current
    if (!node) return
    highlightIn(node)
    if (hasMath(parts.stable)) void renderMathInElement(node)
  })

  useEffect(() => {
    const node = tailRef.current
    if (!node) return
    if (!tailOpen) highlightIn(node)
    // Matematik kuyrukta da render ediliyor: kapanmis bir formul hemen
    // gorunmeli, aksi halde yanit bitene kadar ham LaTeX okunuyor.
    if (hasMath(parts.tail)) void renderMathInElement(node)
  })

  // Imlec DOM'a SONRADAN ekleniyor, React agacina degil: markdown blok
  // seviyesinde bittigi icin (`<li>`, `<p>`...) bir React kardesi olarak
  // eklemek onu her zaman BLOGUN ALTINA duserdi - blok kutular satir ici
  // icerikten sonra her zaman yeni satira geciyor. Gercek son yaprak
  // dugume (en son ic ice `lastElementChild`) eklenince kelimenin TAM
  // ardina, ayni satira oturuyor.
  useEffect(() => {
    if (!streaming) return
    const node = tailRef.current
    if (!node) return
    let leaf: Element = node
    while (leaf.lastElementChild) leaf = leaf.lastElementChild
    const caret = document.createElement('span')
    caret.className = 'caret'
    caret.setAttribute('aria-hidden', 'true')
    leaf.appendChild(caret)
    return () => {
      caret.remove()
    }
  }, [tailHtml, streaming])

  return (
    <div className="md">
      {/*
        Iki ayri kap: React kararli onegin DOM'una hic dokunmuyor (HTML'i
        degismiyor), yani hljs/KaTeX'in oraya yazdigi sonuclar KORUNUYOR.
        Tek kap olsa her frame `innerHTML` yeniden yazilir ve tum
        renklendirme kaybolup yeniden hesaplanirdi.
      */}
      <div ref={stableRef} dangerouslySetInnerHTML={{ __html: stableHtml }} />
      <div ref={tailRef} dangerouslySetInnerHTML={{ __html: tailHtml }} />
    </div>
  )
}
